/**
 * SectionToolbar — the controls for the selected section.
 *
 * Everything about one section that isn't dragging: what product it is, its
 * colour, which way the boards run, and its exact position and size as numbers.
 * What it offers comes from the retailer's product table (`sectionOptions`), so
 * a product or colour the retailer doesn't carry is simply not there.
 *
 * Purely presentational: every control calls back, and the workspace decides
 * whether the change is allowed (and says why when it isn't).
 */

import React from 'react';
import { Copy, Trash2 } from 'lucide-react';
import { Section, Surface, SectionProductType } from '../../types/sections';
import { ProductColor, PanelOrientation } from '../../types';
import { getProductColorHex } from '../ui/room3d/materialFactory';
import { SLATWALL_COVERAGE_HEIGHT } from '../../calculations/constants';
import { MIN_SECTION_SIZE_FT } from '../../calculations/sections';
import { getAvailableColors, getAvailableProductTypes, PRODUCT_TYPE_LABEL } from './sectionOptions';
import { RectFields, RectField, RectPatch } from './RectFields';
import { DESIGNER_STRINGS as S } from './strings';

export type { RectField, RectPatch };

export interface SectionToolbarProps {
  section: Section;
  surface: Surface;
  /** The surface is drawn mirrored (left / right walls), so its +x runs screen-left. */
  xFlipped: boolean;
  onProductType: (type: SectionProductType) => void;
  onColor: (color: ProductColor) => void;
  onOrientation: (orientation: PanelOrientation) => void;
  /** `field` is the number box that was edited (for folding repeated steps into one undo). */
  onRect: (patch: RectPatch, field: RectField) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Extra action buttons, placed between duplicate and delete (align / distribute). */
  actions?: React.ReactNode;
}

const AMBER = '#FEB100';
export const toolbarDivider = <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)' }} />;

export const chipStyle = (active: boolean): React.CSSProperties => ({
  height: 30, padding: '0 10px', borderRadius: 7, cursor: 'pointer',
  fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
  background: active ? 'rgba(254,177,0,0.18)' : 'rgba(255,255,255,0.06)',
  border: active ? `2px solid ${AMBER}` : '2px solid rgba(255,255,255,0.25)',
});

export const iconChipStyle: React.CSSProperties = {
  ...chipStyle(false), width: 30, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

/** SlatWall height moves in whole rows: turn a typed / stepped value into the row count meant. */
function toWholeRows(value: number, current: number): number {
  const step = SLATWALL_COVERAGE_HEIGHT;
  // A one-inch step from the steppers means "one more / one fewer row".
  if (Math.abs(value - current) < step - 1e-6 && Math.abs(value - current) > 1e-6) {
    return Math.max(step, current + Math.sign(value - current) * step);
  }
  return Math.max(step, Math.round(value / step) * step);
}

export const SectionToolbar: React.FC<SectionToolbarProps> = ({
  section,
  surface,
  xFlipped,
  onProductType,
  onColor,
  onOrientation,
  onRect,
  onDuplicate,
  onDelete,
  actions,
}) => {
  const isWCB = section.productType === 'WCB';
  const isCeiling = surface.kind === 'ceiling';
  const orientation: PanelOrientation = section.orientation ?? 'Horizontal';
  const types = getAvailableProductTypes(surface.kind);
  const colors = getAvailableColors(section.productType);

  return (
    // Two centred rows. The workspace wraps this in its floating panel.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, color: '#fff', userSelect: 'none', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        {types.length > 1 && (
          <>
            <div style={{ display: 'flex', gap: 6 }}>
              {types.map((t) => (
                <button key={t} type="button" style={chipStyle(section.productType === t)} onClick={() => { if (t !== section.productType) onProductType(t); }}>
                  {PRODUCT_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            {toolbarDivider}
          </>
        )}

        <div style={{ display: 'flex', gap: 6 }}>
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              aria-label={c}
              onClick={() => onColor(c)}
              style={{
                width: 30, height: 30, padding: 0, borderRadius: 7, cursor: 'pointer',
                background: getProductColorHex(c),
                border: section.color === c ? `2px solid ${AMBER}` : '2px solid rgba(255,255,255,0.25)',
                boxShadow: section.color === c ? '0 0 0 2px rgba(254,177,0,0.25)' : 'none',
              }}
            />
          ))}
        </div>

        {isWCB && (
          <>
            {toolbarDivider}
            <div style={{ display: 'flex', gap: 6 }}>
              {(['Horizontal', 'Vertical'] as PanelOrientation[]).map((o) => (
                <OrientationButton
                  key={o}
                  orientation={o}
                  title={isCeiling
                    ? (o === 'Horizontal' ? S.ceilingAlongLength : S.ceilingAlongWidth)
                    : (o === 'Horizontal' ? S.horizontal : S.vertical)}
                  active={orientation === o}
                  onClick={() => onOrientation(o)}
                />
              ))}
            </div>
          </>
        )}

        {toolbarDivider}
        <button type="button" title={S.duplicate} aria-label={S.duplicate} onClick={onDuplicate} style={iconChipStyle}>
          <Copy size={15} />
        </button>
        {actions}
        <button type="button" title={S.deleteSection} aria-label={S.deleteSection} onClick={onDelete} style={iconChipStyle}>
          <Trash2 size={15} />
        </button>
      </div>

      <RectFields
        rect={section}
        surface={surface}
        xFlipped={xFlipped}
        minSize={MIN_SECTION_SIZE_FT}
        snapHeight={isWCB ? undefined : toWholeRows}
        onRect={onRect}
      />
    </div>
  );
};

const OrientationButton: React.FC<{ orientation: PanelOrientation; title: string; active: boolean; onClick: () => void }> = ({ orientation, title, active, onClick }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    style={{
      width: 30, height: 30, padding: 0, borderRadius: 7, cursor: 'pointer',
      background: active ? 'rgba(254,177,0,0.18)' : 'rgba(255,255,255,0.06)',
      border: active ? `2px solid ${AMBER}` : '2px solid rgba(255,255,255,0.25)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {orientation === 'Horizontal' ? (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <line x1="3" y1="5" x2="13" y2="5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="3" y1="8" x2="13" y2="8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="3" y1="11" x2="13" y2="11" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <line x1="5" y1="3" x2="5" y2="13" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="8" y1="3" x2="8" y2="13" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <line x1="11" y1="3" x2="11" y2="13" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    )}
  </button>
);

export default SectionToolbar;
